<template>
  <div style="position: relative;">

    <!-- Section header -->
    <div class="mb-6">
      <div class="ext-section-number">Chained Episodes</div>
      <h1 class="ext-section-title">Post-Acute Care Cascades</h1>
      <p class="ext-section-desc">Patients who have been admitted to two or more acute or post-acute care settings within a rolling 30-day window. An episode begins with an index admission (IP, ED, or OBS) and chains forward — each subsequent admit within 30 days of the prior discharge extends the episode. The result is a timeline of cascading care events per patient, revealing patterns of readmission, SNF transfers, and ED recidivism. Longer chains and shorter gaps between events signal higher-acuity patients and potential care coordination failures. This view only shows episodes with 2+ events.</p>
    </div>

    <div style="margin-bottom:12px;">
      <select v-model="careSetting" class="px-2 py-1.5 text-sm border border-gray-300 rounded bg-white" @change="fetchData">
        <option value="">All Settings</option>
        <option value="SNF">SNF</option>
        <option value="ALF">ALF</option>
      </select>
    </div>

    <!-- Explainer banner -->
    <div class="toc-banner">
      <div class="toc-banner__legend">
        <span v-for="t in legendItems" :key="t.code" class="toc-legend-chip" :style="{ background: eventColor(t.code).bg, color: eventColor(t.code).text }">{{ t.code }}<span class="toc-legend-label">{{ t.label }}</span></span>
        <span class="toc-legend-chip" style="background: transparent; border: 1px dashed #CFC8BE; color: #A9A29A;">3d<span class="toc-legend-label">Gap between events</span></span>
        <span class="toc-badge toc-badge--hospice">Hospice<span class="toc-legend-label" style="margin-left:4px;">Active enrollment</span></span>
        <span class="toc-badge toc-badge--acsc">ACSC<span class="toc-legend-label" style="margin-left:4px;">Avoidable admission</span></span>
      </div>
      <div class="toc-banner__note">Click any episode row to view claim-level detail including diagnoses, DRG, providers, and line items.</div>
    </div>

    <div v-if="loading" style="padding: 40px; text-align: center; color: #A9A29A; font-size: 13px;">Loading episodes…</div>
    <div v-else-if="!episodes.length" style="padding: 40px; text-align: center; color: #A9A29A; font-size: 13px;">No multi-event episodes found.</div>
    <div v-else>
      <div class="toc-filters">
        <div class="toc-filter-group">
          <label class="toc-filter-label">Year</label>
          <select v-model="selectedYear" class="toc-filter-select">
            <option :value="null">All Years</option>
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
        <div class="toc-filter-group">
          <label class="toc-filter-label">Facility</label>
          <select v-model="selectedFacility" class="toc-filter-select">
            <option :value="null">All Facilities</option>
            <option v-for="f in facilityOptions" :key="f.name" :value="f.name">{{ f.name }} ({{ f.count }})</option>
          </select>
        </div>
        <div class="toc-filter-group">
          <label class="toc-filter-label" style="cursor:pointer;">
            <input type="checkbox" v-model="hospiceOnly" style="margin-right:4px; vertical-align:middle;" />
            Hospice only
          </label>
        </div>
        <div class="toc-filter-group">
          <label class="toc-filter-label" style="cursor:pointer;">
            <input type="checkbox" v-model="readmitOnly" style="margin-right:4px; vertical-align:middle;" />
            Readmissions only
          </label>
        </div>
        <div class="toc-filter-count">{{ filteredEpisodes.length }} episodes</div>
        <button class="toc-export-btn" @click="saveView">{{ savedViewJustSaved ? 'Saved ✓' : 'Save View' }}</button>
        <button v-if="hasSavedView" class="toc-export-btn" @click="resetView">Reset</button>
        <ExportMenu
          :disabled="!filteredEpisodes.length"
          :exports="[
            { type: 'csv', action: exportCsv },
            { type: 'pdf', action: exportPdf },
          ]"
        />
      </div>
      <div class="ext-table-wrap toc-table-wrap" style="overflow: auto; max-height: 70vh;">
      <table class="ext-table toc-table" style="white-space: nowrap;">
        <thead>
          <tr>
            <th class="toc-sticky-col toc-sticky-head toc-col-patient" style="padding-left: 16px; cursor: pointer; user-select: none;" @click="toggleSort('patient')">Patient <span class="toc-sort-icon sensitive">{{ sortIcon('patient') }}</span></th>
            <th style="cursor: pointer; user-select: none;" @click="toggleSort('start')">Start <span class="toc-sort-icon">{{ sortIcon('start') }}</span></th>
            <th style="cursor: pointer; user-select: none;" @click="toggleSort('end')">End <span class="toc-sort-icon">{{ sortIcon('end') }}</span></th>
            <th class="toc-col-facility" style="cursor: pointer; user-select: none;" @click="toggleSort('facility')">Patient Facility <span class="toc-sort-icon sensitive">{{ sortIcon('facility') }}</span></th>
            <th style="cursor: pointer; user-select: none;" @click="toggleSort('events')">Episode Chain <span class="toc-sort-icon">{{ sortIcon('events') }}</span></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="ep in episodes"
            :key="ep.key"
            class="toc-row"
            :class="{ 'is-selected': selectedEpisode && selectedEpisode.key === ep.key }"
            @click="selectEpisode(ep)"
          >
            <td class="toc-sticky-col toc-col-patient" style="padding-left: 16px;">
              <div class="toc-patient-name sensitive" :title="ep.patientName">{{ ep.patientName }}</div>
              <div class="sensitive" style="font-size: 10px; color: #A9A29A; font-family: 'JetBrains Mono', monospace; margin-top: 1px;">{{ ep.mbi }}</div>
              <!-- Badges always on their own row beneath the name/MBI. -->
              <div v-if="ep.hasHospice || ep.hasReadmit || ep.acscConditions.length" class="toc-badge-row">
                <span v-if="ep.hasHospice" class="toc-badge toc-badge--hospice" title="Patient had a hospice enrollment period overlapping this episode — see band above the timeline">Hospice</span>
                <span v-if="ep.hasReadmit" class="toc-badge toc-badge--readmit" title="Contains a readmission — an inpatient admit within 30 days of a prior inpatient discharge">Readmit</span>
                <span v-for="c in ep.acscConditions" :key="c" class="toc-badge toc-badge--acsc" :title="'Avoidable admission: ' + c">{{ c }}</span>
              </div>
            </td>
            <td class="num" style="font-size: 11px;">{{ formatDate(ep.episodeStart) }}</td>
            <td class="num" style="font-size: 11px;">{{ formatDate(ep.episodeEnd) }}</td>
            <td class="toc-col-facility sensitive" style="font-size: 11px;" :title="ep.patientFacility || ''">{{ ep.patientFacility || '—' }}</td>
            <td style="padding: 8px 12px;">
              <div style="position: relative; display: inline-block; white-space: nowrap;" :style="{ paddingTop: ep.hospiceBands.length ? '10px' : '0' }">
                <!-- Hospice enrollment band(s), aligned to the same 5px/day axis -->
                <span
                  v-for="(band, bi) in ep.hospiceBands"
                  :key="'hb-' + bi"
                  class="toc-hospice-band"
                  :style="{ left: band.left + 'px', width: band.width + 'px' }"
                  :title="'Hospice enrollment ' + formatDate(band.start) + ' → ' + formatDate(band.end)"
                />
                <template v-for="(evt, idx) in ep.events" :key="evt.event_index">
                  <span
                    v-if="idx > 0 && evt.days_since_prev > 0"
                    class="toc-gap"
                    :style="{ width: Math.max(18, evt.days_since_prev * 5) + 'px' }"
                  >{{ evt.days_since_prev }}d</span>
                  <span v-else-if="idx > 0" style="display: inline-block; width: 2px; vertical-align: middle;" />
                  <span
                    class="toc-event"
                    :style="{
                      width: Math.max(22, durationDays(evt) * 5) + 'px',
                      color: eventColor(evt.event_type).text,
                      backgroundColor: eventColor(evt.event_type).bg,
                      borderLeft: evt.event_type === 'IP' && evt.clm_admsn_type_cd === 'EMERGENCY' ? '3px solid #f59e0b' : 'none',
                      outline: selectedEvent === evt ? '2px solid #4C6E97' : 'none',
                      outlineOffset: '1px',
                    }"
                    :title="(evt.acsc_condition ? '⚠ ACSC: ' + evt.acsc_condition + ' — ' : '') + evt.event_type + ' ' + formatDate(evt.admit_dt) + ' → ' + formatDate(evt.discharge_dt)"
                    @click.stop="selectEvent(ep, evt)"
                  ><span v-if="evt.acsc_condition" style="font-size:7px; margin-right:1px;">⚠</span>{{ evt.event_type }}</span>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
      <div v-if="episodes.length < filteredEpisodes.length" style="padding: 12px; text-align: center;">
        <button
          @click="visibleCount += 200"
          class="toc-load-more"
        >Load more ({{ filteredEpisodes.length - episodes.length }} remaining)</button>
      </div>
    </div>

    <!-- Sidebar overlay -->
    <div v-if="selectedEpisode" style="position: fixed; inset: 0; z-index: 40;" @click="closeSidebar" />

    <!-- Sidebar -->
    <transition name="slide">
      <div
        v-if="selectedEpisode"
        style="position: fixed; top: 0; right: 0; width: 520px; height: 100vh; background: white; border-left: 1px solid #e5e7eb; box-shadow: -8px 0 24px rgba(0,0,0,0.06); z-index: 50; display: flex; flex-direction: column;"
      >
        <!-- Header -->
        <div style="padding: 20px 24px; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; background: #fafbfc;">
          <div style="display: flex; align-items: flex-start; justify-content: space-between;">
            <div>
              <div class="sensitive" style="font-size: 16px; font-weight: 600; color: #111827; letter-spacing: -0.01em;">{{ selectedEpisode.patientName }}</div>
              <div style="display: flex; align-items: center; gap: 12px; margin-top: 4px; font-size: 11px; color: #6b7280;">
                <span class="sensitive" style="font-family: monospace;">{{ selectedEpisode.mbi }}</span>
                <span v-if="selectedEpisode.age">{{ selectedEpisode.age }}y {{ selectedEpisode.sex }}</span>
                <span class="sensitive" v-if="selectedEpisode.dob">DOB {{ formatDate(selectedEpisode.dob) }}</span>
              </div>
              <div v-if="selectedEpisode.patientFacility" style="font-size: 11px; color: #6b7280; margin-top: 3px;">
                <span style="color: #9ca3af;">Attributed:</span> <span class="sensitive">{{ selectedEpisode.patientFacility }}</span>
              </div>
              <div v-if="selectedEpisode.hasHospice || selectedEpisode.acscConditions.length" style="display: flex; gap: 4px; margin-top: 5px;">
                <span v-if="selectedEpisode.hasHospice" class="toc-badge toc-badge--hospice">Hospice</span>
                <span v-for="c in selectedEpisode.acscConditions" :key="c" class="toc-badge toc-badge--acsc">{{ c }}</span>
              </div>
            </div>
            <button
              @click="closeSidebar"
              style="background: none; border: none; cursor: pointer; font-size: 14px; color: #9ca3af; padding: 4px 8px; border-radius: 4px; line-height: 1;"
              @mouseenter="$event.currentTarget.style.background = '#f3f4f6'"
              @mouseleave="$event.currentTarget.style.background = 'none'"
            >✕</button>
          </div>
        </div>

        <!-- Episode summary -->
        <div style="padding: 16px 24px; border-bottom: 1px solid #f3f4f6; flex-shrink: 0;">
          <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 10px;">
            <div style="font-size: 12px; font-weight: 600; color: #374151;">
              {{ formatDate(selectedEpisode.episodeStart) }} → {{ formatDate(selectedEpisode.episodeEnd) }}
            </div>
            <div style="display: flex; gap: 12px; font-size: 11px; color: #6b7280;">
              <span>{{ selectedEpisode.events.length }} events</span>
              <span>{{ episodeDays }} days</span>
              <span v-if="episodeCost" style="font-weight: 500; color: #111827;">{{ episodeCost }}</span>
            </div>
          </div>
          <!-- Hospice enrollment period(s) — shown as explicit spans, not a
               per-event flag, since hospice runs parallel to the episode -->
          <div v-if="selectedEpisode.hospiceBands && selectedEpisode.hospiceBands.length" style="margin: 2px 0 6px; font-size: 11px; color: #7c5e10;">
            <span style="display:inline-block; width:8px; height:8px; border-radius:2px; background:#f5d67e; margin-right:5px; vertical-align:middle;" />
            Hospice enrollment:
            <span v-for="(band, bi) in selectedEpisode.hospiceBands" :key="'sbh-' + bi" style="font-weight:500;">
              {{ formatDate(band.start) }} → {{ formatDate(band.end) }}<span v-if="bi < selectedEpisode.hospiceBands.length - 1">, </span>
            </span>
          </div>
          <!-- Chain visualization -->
          <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 2px; padding: 8px 0;">
            <template v-for="(evt, idx) in selectedEpisode.events" :key="'chain-' + evt.event_index">
              <span
                v-if="idx > 0 && evt.days_since_prev > 0"
                style="display: inline-flex; align-items: center; justify-content: center; font-size: 8px; color: #9ca3af; background: #f9fafb; border: 1px dashed #e5e7eb; border-radius: 3px;"
                :style="{ width: Math.max(20, evt.days_since_prev * 3) + 'px', height: '26px' }"
              >{{ evt.days_since_prev }}d</span>
              <span v-else-if="idx > 0" style="display: inline-block; width: 2px;" />
              <span
                style="display: inline-flex; align-items: center; justify-content: center; border-radius: 4px; font-size: 10px; font-weight: 700; cursor: pointer; transition: all 0.15s; position: relative;"
                :style="{
                  minWidth: Math.max(28, durationDays(evt) * 3) + 'px',
                  height: '26px',
                  padding: '0 5px',
                  color: eventColor(evt.event_type).text,
                  backgroundColor: selectedEvent === evt ? eventColor(evt.event_type).activeBg : eventColor(evt.event_type).bg,
                  borderLeft: evt.event_type === 'IP' && evt.clm_admsn_type_cd === 'EMERGENCY' ? '3px solid #f59e0b' : 'none',
                  boxShadow: selectedEvent === evt ? '0 0 0 2px #3b82f6' : 'none',
                }"
                @click="selectedEvent = evt; fetchEventDetail(selectedEpisode, evt)"
              >{{ evt.event_type }}</span>
            </template>
          </div>
        </div>

        <!-- Selected event detail -->
        <div v-if="selectedEvent" style="flex: 1; overflow-y: auto; padding: 20px 24px;">
          <!-- Event header -->
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 20px;">
            <span
              style="display: inline-flex; align-items: center; justify-content: center; border-radius: 5px; font-size: 11px; font-weight: 700; padding: 5px 12px; letter-spacing: 0.02em;"
              :style="{ color: eventColor(selectedEvent.event_type).text, backgroundColor: eventColor(selectedEvent.event_type).bg }"
            >{{ selectedEvent.event_type }}</span>
            <span style="font-size: 13px; font-weight: 600; color: #111827;">
              Event {{ selectedEvent.event_index + 1 }} of {{ selectedEpisode.events.length }}
            </span>
          </div>

          <!-- Basic fields -->
          <div style="background: #f9fafb; border-radius: 6px; padding: 12px 16px; margin-bottom: 20px;">
            <table style="width: 100%; border-collapse: collapse;">
              <tr v-for="field in eventFields" :key="field.label">
                <td style="padding: 5px 0; font-size: 11px; color: #6b7280; width: 110px; vertical-align: top;">{{ field.label }}</td>
                <td style="padding: 5px 0; font-size: 12px; color: #111827; font-weight: 450;">{{ field.value }}</td>
              </tr>
            </table>
          </div>

          <!-- Graph detail -->
          <div v-if="detailLoading" style="display: flex; align-items: center; gap: 8px; font-size: 11px; color: #9ca3af; padding: 16px 0;">
            <span style="display: inline-block; width: 14px; height: 14px; border: 2px solid #e5e7eb; border-top-color: #3b82f6; border-radius: 50%; animation: spin 0.6s linear infinite;" />
            Loading claim detail…
          </div>
          <template v-else-if="eventDetail">
            <!-- Claim info -->
            <div v-if="eventDetail.claims && eventDetail.claims.length" style="margin-bottom: 20px;">
              <div style="font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;">Claim Detail</div>
              <div style="background: #f9fafb; border-radius: 6px; padding: 12px 16px;">
                <table style="width: 100%; border-collapse: collapse;">
                  <tr v-for="field in claimFields" :key="field.label">
                    <td style="padding: 4px 0; font-size: 11px; color: #6b7280; width: 110px; vertical-align: top;">{{ field.label }}</td>
                    <td style="padding: 4px 0; font-size: 11px; color: #111827; font-weight: 450;">{{ field.value }}</td>
                  </tr>
                </table>
              </div>
            </div>

            <!-- Diagnoses -->
            <div v-if="eventDetail.diagnoses && eventDetail.diagnoses.length" style="margin-bottom: 20px;">
              <div style="font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;">Diagnoses <span style="font-weight: 400; color: #9ca3af;">{{ eventDetail.diagnoses.length }}</span></div>
              <div v-for="dx in eventDetail.diagnoses" :key="dx.CODE" style="display: flex; align-items: baseline; gap: 10px; padding: 4px 0; font-size: 11px;">
                <span style="font-family: monospace; color: #3b82f6; font-weight: 500; flex-shrink: 0; min-width: 52px;">{{ dx.CODE }}</span>
                <span style="color: #374151;">{{ dx.SHORT_DESCRIPTION || dx.LONG_DESCRIPTION || '' }}</span>
              </div>
            </div>

            <!-- Providers -->
            <div v-if="eventDetail.providers && eventDetail.providers.length" style="margin-bottom: 20px;">
              <div style="font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;">Providers</div>
              <div v-for="p in eventDetail.providers" :key="p.NPI" style="padding: 5px 0; font-size: 11px; display: flex; align-items: center; gap: 10px;">
                <span class="sensitive" style="color: #111827; font-weight: 500;">{{ p.PROVIDER_NAME || '' }}</span>
                <span class="sensitive" style="font-family: monospace; color: #9ca3af; font-size: 10px;">{{ p.NPI }}</span>
                <span v-if="p.SPECIALTY" style="color: #6b7280; font-size: 10px; background: #f3f4f6; padding: 1px 6px; border-radius: 3px;">{{ p.SPECIALTY }}</span>
              </div>
            </div>

            <!-- Organizations -->
            <div v-if="eventDetail.organizations && eventDetail.organizations.length" style="margin-bottom: 20px;">
              <div style="font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;">Facility</div>
              <div v-for="o in eventDetail.organizations" :key="o.CCN || o.NAME" style="padding: 4px 0; font-size: 11px;">
                <span style="color: #111827; font-weight: 500;">{{ o.NAME || '' }}</span>
                <span v-if="o.CCN" style="font-family: monospace; color: #9ca3af; margin-left: 8px; font-size: 10px;">{{ o.CCN }}</span>
              </div>
            </div>

            <!-- Line items -->
            <div v-if="eventDetail.items && eventDetail.items.length" style="margin-bottom: 20px;">
              <div style="font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;">Line Items <span style="font-weight: 400; color: #9ca3af;">{{ eventDetail.items.length }}</span></div>
              <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
                <thead>
                  <tr style="border-bottom: 1px solid #e5e7eb;">
                    <th style="text-align: left; padding: 6px 6px 6px 0; font-size: 10px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em;">Code</th>
                    <th style="text-align: left; padding: 6px; font-size: 10px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em;">Description</th>
                    <th style="text-align: right; padding: 6px 0 6px 6px; font-size: 10px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em;">Allowed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in eventDetail.items" :key="i" style="border-bottom: 1px solid #f9fafb;">
                    <td style="padding: 4px 6px 4px 0; font-family: monospace; color: #3b82f6; font-weight: 500;">{{ item.CODE || '—' }}</td>
                    <td style="padding: 4px 6px; color: #374151;">{{ item.hcpcs_description || item.rev_ctr_description || '' }}</td>
                    <td style="padding: 4px 0 4px 6px; text-align: right; color: #111827; font-weight: 500;">{{ item.ALLOWED_AMT ? '$' + Number(item.ALLOWED_AMT).toLocaleString() : '' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="!eventDetail.claims || !eventDetail.claims.length" style="font-size: 11px; color: #9ca3af; padding: 16px 0; text-align: center;">
              No matching claim found in graph for this event.
            </div>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import jsPDF from 'jspdf'
import { autoTable } from 'jspdf-autotable'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { exportBlockedInDemoMode } from '@/utils/demoSpoof'
import { exportCsv as sharedExportCsv } from '@/components/External/table'
import savedView from '@/mixins/savedView'


export default {
  name: 'External_TOC_Page',
  components: { ExportMenu },
  mixins: [savedView],
  savedView: {
    key: 'toc-chained-episodes',
    fields: ['selectedYear', 'selectedFacility', 'hospiceOnly', 'readmitOnly', 'sortKey', 'sortDir'],
    scopeProp: 'entityId',
  },
  props: {
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      careSetting: '',
      selectedYear: null,
      loading: true,
      rows: [],
      hospiceSpans: {},
      visibleCount: 200,
      selectedEpisode: null,
      selectedEvent: null,
      eventDetail: null,
      detailLoading: false,
      sortKey: 'events',
      sortDir: -1,
      selectedFacility: null,
      hospiceOnly: false,
      readmitOnly: false,
      legendItems: [
        { code: 'IP', label: 'Inpatient' },
        { code: 'ED', label: 'Emergency' },
        { code: 'OBS', label: 'Observation' },
        { code: 'SNF', label: 'Skilled Nursing' },
        { code: 'HHA', label: 'Home Health' },
      ],
    };
  },
  computed: {
    allEpisodes() {
      const map = {};
      for (const r of this.rows) {
        const key = `${r.bene_mbi_id}_${r.episode_num}`;
        if (!map[key]) {
          map[key] = {
            key,
            mbi: r.bene_mbi_id,
            patientName: [r.bene_first_name, r.bene_last_name].filter(Boolean).join(' ') || r.bene_mbi_id,
            age: r.bene_age ? Math.round(r.bene_age) : null,
            sex: r.bene_sex_cd == 1 ? 'M' : r.bene_sex_cd == 2 ? 'F' : '',
            dob: r.bene_dob,
            patientFacility: r.patient_facility,
            episodeStart: r.episode_start,
            episodeEnd: r.episode_end,
            hasHospice: false,
            acscConditions: new Set(),
            events: [],
          };
        }
        if (r.acsc_condition) map[key].acscConditions.add(r.acsc_condition)
        map[key].events.push(r);
      }
      const list = Object.values(map).filter(e => e.events.length > 1)
      list.forEach(ep => {
        ep.acscConditions = [...ep.acscConditions]
        // Hospice bands: position each enrollment span on the same pixel axis
        // the event chips use (5px/day, gaps included). Rendered as a parallel
        // band above the chain — hospice is a continuous period, not an event
        // property.
        ep.hospiceBands = this.computeHospiceBands(ep)
        ep.hasHospice = ep.hospiceBands.length > 0
        // Readmission = an IP admit within 30 days of a PRIOR IP discharge in
        // the same episode. Events are chained in order; scan for IP→…→IP gaps.
        const ipEvents = ep.events
          .filter(e => e.event_type === 'IP' && e.admit_dt && e.discharge_dt)
          .sort((a, b) => (a.admit_dt || '').localeCompare(b.admit_dt || ''))
        ep.hasReadmit = false
        for (let i = 1; i < ipEvents.length; i++) {
          const prevDisc = new Date(ipEvents[i - 1].discharge_dt + 'T00:00:00')
          const thisAdmit = new Date(ipEvents[i].admit_dt + 'T00:00:00')
          const gap = Math.round((thisAdmit - prevDisc) / 86400000)
          if (gap >= 0 && gap <= 30) { ep.hasReadmit = true; break }
        }
      })
      const key = this.sortKey
      const dir = this.sortDir
      return list.sort((a, b) => {
        let av, bv
        if (key === 'patient') { av = a.patientName.toLowerCase(); bv = b.patientName.toLowerCase() }
        else if (key === 'start') { av = a.episodeStart || ''; bv = b.episodeStart || '' }
        else if (key === 'end') { av = a.episodeEnd || ''; bv = b.episodeEnd || '' }
        else if (key === 'facility') { av = (a.patientFacility || '').toLowerCase(); bv = (b.patientFacility || '').toLowerCase() }
        else { av = a.events.length; bv = b.events.length }
        return av < bv ? -dir : av > bv ? dir : 0
      })
    },
    yearOptions() {
      const s = new Set()
      for (const ep of this.allEpisodes) {
        if (ep.episodeStart) s.add(ep.episodeStart.slice(0, 4))
      }
      return [...s].sort().reverse()
    },
    facilityOptions() {
      const counts = {};
      for (const ep of this.allEpisodes) {
        const f = ep.patientFacility || '(none)';
        counts[f] = (counts[f] || 0) + 1;
      }
      return Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredEpisodes() {
      let list = this.allEpisodes
      if (this.selectedYear) list = list.filter(ep => ep.episodeStart && ep.episodeStart.slice(0, 4) === this.selectedYear)
      if (this.selectedFacility) list = list.filter(ep => (ep.patientFacility || '(none)') === this.selectedFacility)
      if (this.hospiceOnly) list = list.filter(ep => ep.hasHospice)
      if (this.readmitOnly) list = list.filter(ep => ep.hasReadmit)
      return list
    },
    episodes() { return this.filteredEpisodes.slice(0, this.visibleCount); },
    episodeDays() {
      if (!this.selectedEpisode) return '';
      const s = new Date(this.selectedEpisode.episodeStart);
      const e = new Date(this.selectedEpisode.episodeEnd);
      return Math.ceil((e - s) / 86400000);
    },
    eventFields() {
      const e = this.selectedEvent;
      if (!e) return [];
      return [
        { label: 'Admit', value: this.formatDate(e.admit_dt) },
        { label: 'Discharge', value: this.formatDate(e.discharge_dt) },
        { label: 'Duration', value: this.durationDays(e) + ' days' },
        { label: 'Facility', value: e.facility_name || e.facility_ccn || '—' },
        e.paid_amount ? { label: 'Paid', value: '$' + Number(e.paid_amount).toLocaleString() } : null,
        e.clm_admsn_type_cd ? { label: 'Admission', value: e.clm_admsn_type_cd } : null,
        e.acsc_condition ? { label: 'ACSC', value: e.acsc_condition } : null,
        e.event_index > 0 && e.days_since_prev != null ? { label: 'Gap from Prior', value: e.days_since_prev + ' days' } : null,
      ].filter(Boolean);
    },
    claimFields() {
      if (!this.eventDetail || !this.eventDetail.claims || !this.eventDetail.claims.length) return [];
      const c = this.eventDetail.claims[0];
      return [
        { label: 'Claim ID', value: c.CLM_ID },
        { label: 'Type', value: [c.CLM_TYPE_CD, c.CLM_TYPE_LABEL].filter(Boolean).join(' — ') },
        c.CLM_PMT_AMT ? { label: 'Payment', value: '$' + Number(c.CLM_PMT_AMT).toLocaleString() } : null,
        c.CLM_ALLOWED_AMT ? { label: 'Allowed', value: '$' + Number(c.CLM_ALLOWED_AMT).toLocaleString() } : null,
        c.DGNS_DRG_CD ? { label: 'DRG', value: c.DGNS_DRG_CD } : null,
        c.BENE_PTNT_STUS_CD ? { label: 'Discharge Status', value: c.BENE_PTNT_STUS_CD } : null,
        c.CLM_ADMSN_SRC_CD ? { label: 'Admission Source', value: c.CLM_ADMSN_SRC_CD } : null,
        c.PAYER ? { label: 'Payer', value: c.PAYER } : null,
      ].filter(Boolean);
    },
    episodeCost() {
      if (!this.selectedEpisode) return '';
      const total = this.selectedEpisode.events.reduce((sum, evt) => sum + (Number(evt.paid_amount) || 0), 0);
      return total ? '$' + total.toLocaleString() : '';
    },
  },
  watch: { entityId() { this.fetchData(); } },
  mounted() { this.fetchData(); },
  methods: {
    onResetView() {
      this.selectedYear = null
      this.selectedFacility = null
      this.hospiceOnly = false
      this.readmitOnly = false
      this.sortKey = 'events'
      this.sortDir = -1
    },
    toggleSort(key) {
      if (this.sortKey === key) { this.sortDir *= -1 }
      else { this.sortKey = key; this.sortDir = key === 'patient' ? 1 : -1 }
    },
    // Hex → [r,g,b] for jsPDF's setFillColor/setTextColor/setDrawColor.
    hexToRgb(hex) {
      const h = hex.replace('#', '')
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
    },
    // Full event-type label for the vertical PDF (the on-screen chain only has
    // room for the code; the vertical layout has width to spell it out).
    eventTypeLabel(type) {
      const item = this.legendItems.find(t => t.code === type)
      return item ? item.label : type
    },
    exportCsv() {
      // Parity via the SHARED exporter (was PDF-only). Flatten episodes → one row
      // per event (line-by-line), matching the PDF's grain, so the CSV is
      // analyzable. Shared exportCsv handles date local-parsing + escaping.
      const flat = []
      this.filteredEpisodes.forEach((ep) => {
        ep.events.forEach((evt, idx) => {
          flat.push({
            patient: idx === 0 ? (ep.patientName || ep.mbi || '') : '',
            mbi: idx === 0 ? ep.mbi : '',
            facility: idx === 0 ? ep.patientFacility : '',
            flags: idx === 0
              ? [...(ep.hasHospice ? ['Hospice'] : []), ...(ep.hasReadmit ? ['Readmit'] : []), ...(ep.acscConditions || [])].join('; ')
              : '',
            event_type: evt.event_type,
            admit_dt: evt.admit_dt,
            discharge_dt: evt.discharge_dt,
            los_days: this.durationDays(evt),
            gap_days: idx > 0 ? (evt.days_since_prev || 0) : '',
            admission_type: evt.clm_admsn_type_cd ? this.titleCase(evt.clm_admsn_type_cd) : '',
            acsc: evt.acsc_condition || '',
          })
        })
      })
      const columns = [
        { field: 'patient', header: 'Patient', type: 'text' },
        { field: 'mbi', header: 'MBI', type: 'text' },
        { field: 'facility', header: 'Facility', type: 'text' },
        { field: 'flags', header: 'Flags', type: 'text' },
        { field: 'event_type', header: 'Event', type: 'text' },
        { field: 'admit_dt', header: 'Admit', type: 'date' },
        { field: 'discharge_dt', header: 'Discharge', type: 'date' },
        { field: 'los_days', header: 'LOS (days)', type: 'number' },
        { field: 'gap_days', header: 'Gap from Prior (days)', type: 'number' },
        { field: 'admission_type', header: 'Admission Type', type: 'text' },
        { field: 'acsc', header: 'ACSC', type: 'text' },
      ]
      const tag = this.selectedFacility ? `_${this.selectedFacility.replace(/[^a-z0-9]+/gi, '_')}` : ''
      sharedExportCsv(flat, columns, { filename: `chained_episodes${tag}.csv` })
    },
    exportPdf() {
      if (exportBlockedInDemoMode()) return
      const rows = this.filteredEpisodes
      if (!rows.length) return

      // Portrait. autotable owns the layout: one BODY ROW PER EVENT, grouped by
      // patient, so a provider reads each stay line-by-line (ED / IP / SNF dates
      // of service) exactly as Jennifer asked. The plugin handles ALL pagination,
      // repeating column headers, and never splits a row across a page — replacing
      // the hand-rolled y-cursor + ensureRoom bookkeeping. The only custom draw is
      // the small colored event badge per row, painted in didDrawCell.
      const doc = new jsPDF('p', 'pt', 'letter')
      const pageW = doc.internal.pageSize.getWidth()
      const marginX = 40

      const ink = this.hexToRgb('#221F1C')
      const faint = this.hexToRgb('#A9A29A')
      const patientBorder = this.hexToRgb('#CFC8BE')

      // Flatten episodes → event rows. The first event of each episode carries the
      // patient/facility/flags (rendered as a group header line via cell styling);
      // continuation events leave those columns blank so the eye groups them.
      const body = []
      rows.forEach((ep) => {
        const totalDays = Math.max(1, Math.ceil(
          (new Date(ep.episodeEnd) - new Date(ep.episodeStart)) / 86400000
        ))
        const flags = [
          ...(ep.hasHospice ? ['Hospice'] : []),
          ...(ep.hasReadmit ? ['Readmit'] : []),
          ...(ep.acscConditions || []),
        ]
        ep.events.forEach((evt, idx) => {
          const dur = this.durationDays(evt)
          const admType = evt.clm_admsn_type_cd ? this.titleCase(evt.clm_admsn_type_cd) : ''
          const detailBits = [
            idx > 0 && evt.days_since_prev > 0 ? `+${evt.days_since_prev}d gap` : null,
            admType || null,
            // Plain-text ACSC tag — jsPDF Helvetica/WinAnsi can't render the warning
            // glyph, it silently drops it.
            evt.acsc_condition ? `ACSC: ${evt.acsc_condition}` : null,
          ].filter(Boolean)
          body.push({
            _isFirst: idx === 0,
            _ep: ep,
            _evt: evt,
            patient: idx === 0 ? (ep.patientName || ep.mbi || '') : '',
            submeta: idx === 0
              ? [ep.mbi, ep.patientFacility].filter(Boolean).join('  \u00b7  ')
                + (flags.length ? '   [' + flags.join(', ') + ']' : '')
              : '',
            event: evt.event_type,                 // badge drawn in didDrawCell
            // ASCII "->" — WinAnsi has no arrow glyph.
            dates: `${this.formatDate(evt.admit_dt)} -> ${this.formatDate(evt.discharge_dt)}`,
            days: `${dur}d`,
            detail: detailBits.join('   \u00b7   '),
          })
        })
        // Episode summary marker on the first row (shown after the name line).
        if (ep.events[0]) {
          const first = body[body.length - ep.events.length]
          if (first) first._episodeSummary =
            `${this.formatDate(ep.episodeStart)} -> ${this.formatDate(ep.episodeEnd)}  \u00b7  ${totalDays} days  \u00b7  ${ep.events.length} events`
        }
      })

      const scope = this.selectedFacility ? ` \u2014 ${this.selectedFacility}` : ''
      const yr = this.selectedYear ? ` (${this.selectedYear})` : ''

      autoTable(doc, {
        startY: 64,
        margin: { top: 64, left: marginX, right: marginX, bottom: 40 },
        head: [['Patient', '', 'Dates of Service', 'LOS', 'Detail']],
        body: body.map(r => [r.patient, r.event, r.dates, r.days, r.detail]),
        theme: 'plain',
        styles: { font: 'Helvetica', fontSize: 8, textColor: ink, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 }, valign: 'middle' },
        headStyles: { fontSize: 7.5, fontStyle: 'bold', textColor: faint, lineWidth: { bottom: 0.75 }, lineColor: patientBorder },
        columnStyles: {
          0: { cellWidth: 150 },
          1: { cellWidth: 42, halign: 'center' },   // event badge column
          2: { cellWidth: 130 },
          3: { cellWidth: 34, halign: 'right' },
          4: { cellWidth: 'auto' },
        },
        // Repeating page title/legend on every page.
        didDrawPage: () => {
          doc.setFont('Helvetica', 'bold')
          doc.setFontSize(14)
          doc.setTextColor(...ink)
          doc.text(`Chained Episodes${scope}${yr}`, marginX, 32)
          doc.setFont('Helvetica', 'normal')
          doc.setFontSize(8)
          doc.setTextColor(...faint)
          doc.text(
            `${rows.length.toLocaleString()} multi-event episodes \u00b7 generated ${new Date().toLocaleDateString('en-US')}`,
            marginX, 46
          )
          // color legend, right-aligned
          let lx = pageW - marginX
          doc.setFontSize(7)
          for (const t of [...this.legendItems].reverse()) {
            const c = this.eventColor(t.code)
            const w = doc.getTextWidth(t.code) + 10
            lx -= w
            doc.setFillColor(...this.hexToRgb(c.bg))
            doc.roundedRect(lx, 24, w, 12, 2, 2, 'F')
            doc.setTextColor(...this.hexToRgb(c.text))
            doc.text(t.code, lx + 5, 32.5)
            lx -= 4
          }
        },
        // Per-row grouping visuals + the custom event badge.
        didParseCell: (data) => {
          if (data.section !== 'body') return
          const r = body[data.row.index]
          if (!r) return
          // Draw a heavier top border on the first event of each patient to
          // visually separate episodes.
          if (r._isFirst && data.row.index > 0) {
            data.cell.styles.lineWidth = Object.assign({}, data.cell.styles.lineWidth, { top: 0.75 })
            data.cell.styles.lineColor = patientBorder
          }
          if (data.column.index === 0 && r._isFirst) {
            data.cell.styles.fontStyle = 'bold'
            data.cell.styles.valign = 'top'
          }
          // Reserve vertical room on the first row of each episode for the two
          // sub-lines (episode summary + MBI/facility/flags) drawn in didDrawCell.
          if (r._isFirst) {
            const extra = (r._episodeSummary ? 8 : 0) + (r.submeta ? 8 : 0)
            data.cell.styles.minCellHeight = Math.max(data.cell.styles.minCellHeight || 0, 14 + extra)
          }
          // The event badge cell renders its text via didDrawCell; hide the raw text.
          if (data.column.index === 1) data.cell.text = ['']
        },
        didDrawCell: (data) => {
          if (data.section !== 'body') return
          const r = body[data.row.index]
          if (!r) return
          // Patient sub-meta + episode summary printed under the bold name.
          if (data.column.index === 0 && r._isFirst && (r.submeta || r._episodeSummary)) {
            const x = data.cell.x + data.cell.padding('left')
            // Name occupies the top ~11pt of the cell; stack sub-lines beneath it.
            let yy = data.cell.y + 18
            doc.setFont('Helvetica', 'normal')
            doc.setFontSize(6.2)
            doc.setTextColor(...faint)
            if (r._episodeSummary) { doc.text(r._episodeSummary, x, yy); yy += 8 }
            if (r.submeta) doc.text(r.submeta, x, yy)
          }
          // Colored event-type badge.
          if (data.column.index === 1) {
            const c = this.eventColor(r.event)
            const bw = 30, bh = 12
            const bx = data.cell.x + (data.cell.width - bw) / 2
            const by = data.cell.y + (data.cell.height - bh) / 2
            doc.setFillColor(...this.hexToRgb(c.bg))
            doc.roundedRect(bx, by, bw, bh, 2, 2, 'F')
            // emergency IP → amber left edge
            if (r.event === 'IP' && r._evt.clm_admsn_type_cd === 'EMERGENCY') {
              doc.setDrawColor(...this.hexToRgb('#f59e0b'))
              doc.setLineWidth(1.4)
              doc.line(bx, by, bx, by + bh)
            }
            doc.setFont('Helvetica', 'bold')
            doc.setFontSize(7)
            doc.setTextColor(...this.hexToRgb(c.text))
            doc.text(r.event, bx + bw / 2, by + bh / 2 + 2.4, { align: 'center' })
          }
        },
        rowPageBreak: 'avoid',
      })

      const tag = this.selectedFacility ? `_${this.selectedFacility.replace(/[^a-z0-9]+/gi, '_')}` : ''
      doc.save(`chained_episodes${tag}.pdf`)
    },
    titleCase(s) {
      return String(s).toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
    },
    sortIcon(key) {
      if (this.sortKey !== key) return '↕'
      return this.sortDir === 1 ? '↑' : '↓'
    },
    eventColor(type) {
      return {
        ED:  { bg: '#fef3c7', text: '#92400e', activeBg: '#fde68a' },
        IP:  { bg: '#ffe4e6', text: '#9f1239', activeBg: '#fecdd3' },
        OBS: { bg: '#dbeafe', text: '#1e40af', activeBg: '#bfdbfe' },
        SNF: { bg: '#f3e8ff', text: '#6b21a8', activeBg: '#e9d5ff' },
        HHA: { bg: '#dcfce7', text: '#166534', activeBg: '#bbf7d0' },
      }[type] || { bg: '#e5e7eb', text: '#374151', activeBg: '#d1d5db' };
    },
    formatDate(val) {
      if (!val) return '—';
      // These are CALENDAR dates (no time, no zone). `new Date("2025-04-08")`
      // parses a date-only string as midnight UTC, which toLocaleDateString then
      // renders in the browser's local zone — shifting the day back by one for
      // any user west of UTC (all US timezones). Parse YYYY-MM-DD from its parts
      // as a LOCAL date so the day in == the day out. (Matches the 'T00:00:00'
      // local-parse convention already used by computeHospiceBands / hasReadmit.)
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(val));
      const d = m
        ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
        : new Date(val);
      return isNaN(d) ? '—' : d.toLocaleDateString('en-US');
    },
    durationDays(evt) {
      if (!evt.discharge_dt || !evt.admit_dt) return 1;
      return Math.max(1, Math.ceil((new Date(evt.discharge_dt) - new Date(evt.admit_dt)) / 86400000));
    },
    selectEpisode(ep) {
      this.selectedEpisode = ep;
      this.selectedEvent = ep.events[0];
      this.fetchEventDetail(ep, ep.events[0]);
    },
    selectEvent(ep, evt) {
      this.selectedEpisode = ep;
      this.selectedEvent = evt;
      this.fetchEventDetail(ep, evt);
    },
    closeSidebar() {
      this.selectedEpisode = null;
      this.selectedEvent = null;
      this.eventDetail = null;
    },
    async fetchEventDetail(ep, evt) {
      this.eventDetail = null;
      this.detailLoading = true;
      try {
        const { data } = await this.$axios.post('/api/portfolio_management/cclf-report/episode-event-detail/', {
          mbi: ep.mbi,
          admit_dt: evt.admit_dt,
          discharge_dt: evt.discharge_dt,
          event_type: evt.event_type,
        });
        this.eventDetail = data;
      } catch (err) {
        console.error(err);
        this.eventDetail = null;
      }
      this.detailLoading = false;
    },
    computeHospiceBands(ep) {
      // Walk the event chain building a pixel coordinate map that mirrors the
      // list template's chip layout (5px/day; event=max(22,dur*5), gap between
      // consecutive events=max(18,days_since_prev*5)). For each event record
      // its [xStart,xEnd] and calendar [dateStart,dateEnd]. Then map each
      // hospice span onto that axis by linear interpolation within the event(s)
      // it overlaps, so the band lines up with where those dates sit visually.
      const spans = this.hospiceSpans[ep.mbi]
      if (!spans || !spans.length) return []
      const PX_PER_DAY = 5, EVENT_MIN = 22, GAP_MIN = 18, MARGIN = 1
      // Chips (event + gap) carry `margin: 0 1px`, so each occupies width+2px of
      // flow. Account for both side-margins to keep the band aligned to later
      // chips (drift compounds without this).
      const segs = []  // {x0, x1, d0(ms), d1(ms)}
      let x = 0
      ep.events.forEach((evt, idx) => {
        if (idx > 0) {
          if (evt.days_since_prev > 0) {
            const g = Math.max(GAP_MIN, evt.days_since_prev * PX_PER_DAY)
            x += MARGIN + g + MARGIN
          } else {
            x += 2  // spacer span, no margins
          }
        }
        const dur = this.durationDays(evt)
        const w = Math.max(EVENT_MIN, dur * PX_PER_DAY)
        x += MARGIN  // left margin of the event chip
        const d0 = evt.admit_dt ? new Date(evt.admit_dt + 'T00:00:00').getTime() : null
        const d1 = evt.discharge_dt ? new Date(evt.discharge_dt + 'T00:00:00').getTime() : d0
        if (d0 != null) segs.push({ x0: x, x1: x + w, d0, d1: Math.max(d1, d0) })
        x += w + MARGIN  // chip body + right margin
      })
      if (!segs.length) return []
      // Interpolate a calendar date (ms) to a pixel x within the segment axis.
      const dateToX = (ms) => {
        // Before first / after last segment: clamp to chain extent.
        if (ms <= segs[0].d0) return segs[0].x0
        const last = segs[segs.length - 1]
        if (ms >= last.d1) return last.x1
        for (const s of segs) {
          if (ms >= s.d0 && ms <= s.d1) {
            const span = s.d1 - s.d0
            const frac = span > 0 ? (ms - s.d0) / span : 0
            return s.x0 + frac * (s.x1 - s.x0)
          }
        }
        // In a gap between two segments: interpolate across the gap.
        for (let i = 1; i < segs.length; i++) {
          const prev = segs[i - 1], next = segs[i]
          if (ms > prev.d1 && ms < next.d0) {
            const span = next.d0 - prev.d1
            const frac = span > 0 ? (ms - prev.d1) / span : 0
            return prev.x1 + frac * (next.x0 - prev.x1)
          }
        }
        return last.x1
      }
      const chainStart = segs[0].d0
      const chainEnd = segs[segs.length - 1].d1
      const bands = []
      for (const sp of spans) {
        const s = new Date(sp.start + 'T00:00:00').getTime()
        const e = new Date(sp.end + 'T00:00:00').getTime()
        // Skip spans entirely outside the chain's date range.
        if (e < chainStart || s > chainEnd) continue
        const left = dateToX(Math.max(s, chainStart))
        const right = dateToX(Math.min(e, chainEnd))
        bands.push({ left, width: Math.max(3, right - left), start: sp.start, end: sp.end })
      }
      return bands
    },
    async fetchData() {
      this.loading = true;
      try {
        const payload = { performance_year: 2026 };
        if (this.entityId) payload.entity_id = this.entityId;
        if (this.careSetting) payload.care_setting = this.careSetting;
        const { data } = await this.$axios.post('/api/portfolio_management/cclf-report/painful-episodes/', payload);
        this.rows = data.rows || [];
        this.hospiceSpans = data.hospice_spans || {};
        this.visibleCount = 200;
      } catch (err) { console.error(err); this.rows = []; this.hospiceSpans = {}; }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Banner */
.toc-banner {
  background: #FFFFFF;
  border: 1px solid #F2EEE8;
  border-radius: 8px;
  padding: 14px 18px;
  margin-bottom: 20px;
}
.toc-banner__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.toc-banner__note {
  font-size: 11.5px;
  color: #A9A29A;
  line-height: 1.4;
}
.toc-legend-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.toc-legend-label {
  font-weight: 400;
  font-size: 10.5px;
  opacity: 0.7;
}

/* Table rows */
.toc-row {
  cursor: pointer;
  transition: background 0.1s;
}
.toc-row:hover {
  background: #FFF4EC !important;
}
.toc-row.is-selected {
  background: #FEE3D9 !important;
}

/* Episode chain elements */
.toc-gap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  color: #A9A29A;
  background: #F9F6F1;
  border: 1px dashed #E8E3DC;
  border-radius: 3px;
  height: 22px;
  margin: 0 1px;
  vertical-align: middle;
}
.toc-event {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.02em;
  height: 22px;
  margin: 0 1px;
  vertical-align: middle;
  cursor: pointer;
  transition: opacity 0.1s;
}
.toc-event:hover {
  opacity: 0.85;
}
/* Hospice enrollment band — runs parallel above the event chain on the same
   pixel axis. Continuous period, not a per-event flag. */
.toc-hospice-band {
  position: absolute;
  top: 0;
  height: 5px;
  border-radius: 2px;
  background: repeating-linear-gradient(
    45deg,
    #f5d67e, #f5d67e 4px,
    #efc94e 4px, #efc94e 8px
  );
  border: 1px solid #d9ad2e;
  z-index: 1;
}
.toc-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
  letter-spacing: 0.02em;
  line-height: 1.4;
  white-space: nowrap;
}
.toc-badge--hospice {
  background: white;
  color: #4C6E97;
  border: 1px solid #4C6E97;
}
.toc-badge--acsc {
  background: white;
  color: #B45309;
  border: 1px solid #B45309;
}
.toc-badge--readmit {
  background: #FEF2F2;
  color: #B91C1C;
  border: 1px solid #F0A9A9;
}

/* Freeze the Patient column while the wide chain scrolls horizontally. */
.toc-table-wrap { position: relative; }
.toc-table { width: auto; }
.toc-sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #FFFFFF;
}
/* Fixed column widths so Patient / Patient Facility don't sprawl to content
   width (they were auto-sized by the widest cell). Patient trimmed ~15%,
   Patient Facility ~30%, giving the Episode Chain more room. Overflowing
   text ellipsizes (full value stays in the cell title tooltip). */
.toc-col-patient { width: 215px; max-width: 215px; }
.toc-col-facility {
  width: 140px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Patient cell stacks: name, MBI, then a dedicated badge row. The name
   ellipsizes within the narrower column; badges ALWAYS sit on their own row
   beneath (wrapping among themselves), never inline with the name. */
.toc-patient-name {
  font-weight: 500;
  color: #221F1C;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.toc-badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 3px;
}
.toc-sticky-head {
  z-index: 3;
}
.toc-table thead th.toc-sticky-col {
  background: #FAF9F6;
}
.toc-row.is-selected .toc-sticky-col { background: #FFF4EC; }
.toc-row:hover .toc-sticky-col { background: #FAF9F6; }

.toc-export-btn {
  font-family: inherit;
  font-size: 11.5px;
  color: #4A4540;
  background: #FFFFFF;
  border: 1px solid #E8E3DC;
  border-radius: 5px;
  padding: 5px 12px;
  cursor: pointer;
}
.toc-export-btn:hover:not(:disabled) { border-color: #CFC8BE; background: #FAF9F6; }
.toc-export-btn:disabled { opacity: 0.5; cursor: default; }

.toc-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.toc-filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}
.toc-filter-label {
  font-size: 11px;
  font-weight: 500;
  color: #7D7770;
}
.toc-filter-select {
  font-family: inherit;
  font-size: 11.5px;
  color: #4A4540;
  background: #FFFFFF;
  border: 1px solid #E8E3DC;
  border-radius: 5px;
  padding: 5px 8px;
  max-width: 300px;
}
.toc-filter-select:hover {
  border-color: #CFC8BE;
}
.toc-filter-count {
  margin-left: auto;
  font-size: 11.5px;
  font-weight: 500;
  color: #A9A29A;
}

.toc-sort-icon {
  font-size: 9px;
  color: #A9A29A;
  margin-left: 2px;
}

.toc-load-more {
  padding: 6px 16px;
  font-size: 11px;
  font-family: inherit;
  color: #7D7770;
  background: transparent;
  border: 1px solid #E8E3DC;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.1s;
}
.toc-load-more:hover {
  background: #F9F6F1;
  color: #0F0D0B;
  border-color: #CFC8BE;
}
</style>
